import type { AxiosInstance } from 'axios';

/**
 * RUM for problems the patient feels inside the booking flow (ghane DR-52).
 *
 * `route_transition` measures moving between pages; this reports what goes wrong *inside*
 * a booking step, from the browser, at the moment it happens — in the same shape as the
 * `FactorIssue` events (`book_error`, …) so both can be read together:
 *
 *   slow_api            a booking call finished, but took longer than its limit
 *   api_failed          network error, timeout or a 5xx answer
 *   left_while_waiting  the page was hidden/closed while a booking call was still pending
 *   api_ok              a small sample of fine calls, so a rate has a denominator
 *
 * It stays silent when things are fine, and outside the booking flow: a call is only
 * watched while a page has set a `step` with `setUxIssueContext`. Limits and the on/off
 * switch live in GrowthBook (`rum:ux-issue`), so they change without a release. Without
 * that feature (or if GrowthBook never loads) the defaults below apply.
 *
 * Privacy: request fields are an allow-list (ids only); `api_url` is cut at the matched
 * endpoint so ids in the path never leave; `current_url` goes without its query string
 * (the receipt page carries an access code there). Patient name, mobile number, national
 * code and tokens are never sent.
 */

export type UxIssueType = 'slow_api' | 'api_failed' | 'left_while_waiting' | 'api_ok';
export type LeaveReason = 'page_hidden' | 'pagehide';

const BOOKING_ENDPOINTS = 'booking:api-endpoints';
const SUBUSER_ENDPOINTS = 'subuser:api-endpoints';

export interface TrackedApi {
  /** Logical name sent as `api` — stable even when GrowthBook remaps the address. */
  name: string;
  /** Key in the GrowthBook endpoint map `feature`, when the address can be remapped. */
  key?: string;
  /** GrowthBook feature holding `key`. Defaults to `booking:api-endpoints`. */
  feature?: string;
  /** Default path. */
  path: string;
  /** The path is a prefix (ids follow it). */
  prefix?: boolean;
  limitMs: number;
}

/**
 * Not covered yet: `/api/addBookToQueue`, `PATCH /v1/book/:id` (finalize),
 * `callBookingAddonEndpoint` and the profile's availability-status call.
 */
export const BOOKING_APIS: TrackedApi[] = [
  // Slot loading: p95 is ~3.3s on a normal day, so the limit sits above that tail.
  { name: 'getFreeTurn', key: 'first_time_available', path: '/api/getFreeTurn', limitMs: 5000 },
  { name: 'getFreeDays', key: 'available_days', path: '/api/getFreeDays', limitMs: 5000 },
  { name: 'getFreeTurns', key: 'available_times', path: '/api/getFreeTurns', limitMs: 5000 },
  { name: 'suspend', key: 'block_slot', path: '/api/suspend', limitMs: 3000 },
  { name: 'unsuspend', key: 'unblock_slot', path: '/api/unsuspend', limitMs: 3000 },
  { name: 'bookRequest', key: 'create_new_request_booking', path: '/api/bookRequest', limitMs: 5000 },
  { name: 'book', key: 'create_new_booking', path: '/api/book', limitMs: 5000 },
  { name: 'listSubUser', key: 'list', feature: SUBUSER_ENDPOINTS, path: '/api/listSubUser', limitMs: 3000 },
  { name: 'addSubUser', key: 'add', feature: SUBUSER_ENDPOINTS, path: '/api/addSubUser', limitMs: 3000 },
  {
    name: 'getUserActiveTurnsCount',
    key: 'get_user_active_turns_count',
    path: '/api/getUserActiveTurnsCount',
    limitMs: 3000,
  },
  { name: 'consultInvoiceDetails', key: 'consult_invoice_details', path: '/api/consultInvoiceDetails', limitMs: 3000 },
  { name: 'discount_inquiry', path: '/api/discount/v1/inquiry', limitMs: 3000 },
  { name: 'discount_inquiry', path: '/v1/discount/inquiry', limitMs: 3000 },
  { name: 'consult_payment', key: 'consult_payment', path: '/consult-payment', limitMs: 4000 },
  { name: 'center_payment', key: 'center_payment', path: '/center-payment', limitMs: 4000 },
  { name: 'getBooks', key: 'visits', path: '/V1/patient/visits', limitMs: 3000 },
  { name: 'user_book', key: 'user_book', path: '/user/v1/book/', prefix: true, limitMs: 3000 },
];

export interface UxIssueConfig {
  enabled?: boolean;
  /** Per-call limit in ms, by logical name. */
  limits?: Record<string, number>;
  /** A pending call younger than this is not "left while waiting". */
  min_wait_ms?: number;
  /** Share of fine calls reported as `api_ok`. */
  ok_sample_rate?: number;
}

export const UX_ISSUE_FEATURE = 'rum:ux-issue';
const DEFAULT_MIN_WAIT_MS = 3000;
const DEFAULT_OK_SAMPLE_RATE = 0.02;
const MESSAGE_MAX = 300;
/** A request that has not come back after this long is forgotten. */
const PENDING_MAX_AGE_MS = 120_000;
const PENDING_MAX = 50;

/** What the page knows and the request layer does not. Set by the booking and invoice pages. */
export interface UxIssueContext {
  step?: string;
  doctor_name?: string;
  center_id?: string;
  service_id?: string;
  user_center_id?: string;
  book_id?: string;
  has_price?: boolean;
}

let context: UxIssueContext = {};

export function setUxIssueContext(next: UxIssueContext) {
  context = { ...context, ...next };
}

export function clearUxIssueContext() {
  context = {};
}

export interface UxIssuePayload extends UxIssueContext {
  event_type: UxIssueType;
  api: string;
  api_url: string | null;
  /** Page address without its query string. */
  current_url: string | null;
  http_status: number | null;
  response_status_code: string | number | null;
  response_message_text: string | null;
  /** Same text under the name `FactorIssue` events use. */
  message: string | null;
  duration_ms?: number;
  limit_ms?: number;
  waited_ms?: number;
  leave_reason?: LeaveReason;
  /** This request was already reported as `left_while_waiting`. */
  left_reported?: boolean;
  reason?: string;
  sample_rate?: number;
  ms_since_page_load: number;
  /** Nth time this call was made for the same doctor and service since the page loaded. */
  attempt: number;
  request_code?: string;
  server_id?: string;
  payment_method?: string;
  request_id: string | null;
  connection: string | null;
  online: boolean | null;
  user_agent: string | null;
  build_id: string | null;
  user_id: string | number | null;
}

type EndpointMapReader = (feature: string) => Record<string, string> | undefined;

const ORIGIN = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]+)(\/[^?#]*)?/i;

const splitUrl = (url: string) => {
  const match = url.match(ORIGIN);
  if (match) return { origin: match[1], path: match[2] ?? '/' };
  return { origin: '', path: url.split('?')[0].split('#')[0] };
};

const lastSegment = (path: string) => path.split('/').filter(Boolean).pop() ?? '';

/** Returns the request path cut at the matched endpoint, or undefined when it does not match. */
const matchPath = (requestPath: string, target: string, prefix?: boolean): string | undefined => {
  const targetPath = splitUrl(target).path;
  if (!targetPath || targetPath === '/') return undefined;
  if (prefix) {
    const at = requestPath.indexOf(targetPath);
    return at === -1 ? undefined : requestPath.slice(0, at + targetPath.length);
  }
  return requestPath === targetPath || requestPath.endsWith(targetPath) ? requestPath : undefined;
};

const matchApi = (url: string | undefined, getEndpointMap: EndpointMapReader) => {
  if (!url || typeof url !== 'string') return undefined;
  const { origin, path } = splitUrl(url);
  for (const api of BOOKING_APIS) {
    const remapped = api.key ? getEndpointMap(api.feature ?? BOOKING_ENDPOINTS)?.[api.key] : undefined;
    const candidates = [api.path];
    // A remapped prefix must still end in the same segment, so a wider remap cannot pull
    // unrelated paths (and the ids in them) into this endpoint.
    if (
      typeof remapped === 'string' &&
      remapped &&
      (!api.prefix || lastSegment(splitUrl(remapped).path) === lastSegment(api.path))
    ) {
      candidates.unshift(remapped);
    }
    for (const candidate of candidates) {
      const safePath = matchPath(path, candidate, api.prefix);
      if (safePath) return { api, safeUrl: `${origin}${safePath}` };
    }
  }
  return undefined;
};

export function resolveApi(url: string | undefined, getEndpointMap: EndpointMapReader): TrackedApi | undefined {
  return matchApi(url, getEndpointMap)?.api;
}

const REQUEST_FIELDS = ['center_id', 'server_id', 'user_center_id', 'service_id', 'request_code', 'book_id', 'payment_method'];

interface RequestLike {
  baseURL?: string;
  url?: string;
  headers?: any;
  params?: any;
  data?: any;
}

const readSource = (source: any, key: string): unknown => {
  if (!source) return undefined;
  if (typeof source.get === 'function') {
    try {
      const value = source.get(key);
      if (value !== null && value !== undefined) return value;
    } catch {
      return undefined;
    }
  }
  if (typeof source === 'object') return source[key];
  return undefined;
};

/** Ids needed to find the same request in server logs. Allow-list only — never patient data. */
export function pickRequestFields(config: RequestLike): Record<string, string> {
  let data = config?.data;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      data = undefined;
    }
  }
  const out: Record<string, string> = {};
  for (const key of REQUEST_FIELDS) {
    for (const source of [config?.headers, config?.params, data]) {
      const value = readSource(source, key);
      if ((typeof value === 'string' || typeof value === 'number') && String(value) !== '') {
        out[key] = String(value).slice(0, 80);
        break;
      }
    }
  }
  return out;
}

const fullUrl = (config: RequestLike): string | undefined => {
  const url = config?.url;
  if (!url) return undefined;
  if (ORIGIN.test(url)) return url;
  return `${(config.baseURL ?? '').replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
};

interface ResponseLike {
  config?: any;
  status?: number;
  headers?: any;
  data?: any;
}

interface ErrorLike {
  config?: any;
  code?: string;
  message?: string;
  response?: ResponseLike;
}

interface Pending {
  api: TrackedApi;
  startedAt: number;
  attempt: number;
  apiUrl: string;
  fields: Record<string, string>;
  /** The page context when the request started — the answer may arrive on another step. */
  context: UxIssueContext;
  leftReported: boolean;
}

export interface UxIssueDeps {
  now: () => number;
  send: (payload: UxIssuePayload) => void;
  getConfig: () => UxIssueConfig | undefined;
  getEndpointMap: EndpointMapReader;
  random: () => number;
  getDevice: () => Pick<UxIssuePayload, 'connection' | 'online' | 'user_agent' | 'build_id' | 'user_id' | 'current_url'>;
}

const MARK = '__uxIssueId';

/**
 * The detector, free of axios and the DOM so it can be tested. Monitoring must never hurt
 * the booking request: every entry point swallows its own failures.
 */
export function createUxIssueDetector(deps: UxIssueDeps) {
  const pending = new Map<number, Pending>();
  const attempts = new Map<string, number>();
  let nextId = 1;

  const config = (): UxIssueConfig => deps.getConfig() ?? {};

  const bodyFields = (response?: ResponseLike) => {
    const body = response?.data && typeof response.data === 'object' ? response.data : undefined;
    const status = body?.status;
    const message = typeof body?.message === 'string' ? body.message.slice(0, MESSAGE_MAX) : null;
    const requestId = response?.headers?.['x-request-id'];
    return {
      http_status: typeof response?.status === 'number' ? response.status : null,
      response_status_code: typeof status === 'string' || typeof status === 'number' ? status : null,
      response_message_text: message,
      message,
      request_id: typeof requestId === 'string' ? requestId : null,
      book_id: typeof body?.book_info?.id === 'string' ? body.book_info.id : undefined,
    };
  };

  const emit = (type: UxIssueType, entry: Pending, extra: Partial<UxIssuePayload>, response?: ResponseLike) => {
    const body = bodyFields(response);
    const payload: UxIssuePayload = {
      event_type: type,
      ...entry.context,
      ...entry.fields,
      api: entry.api.name,
      api_url: entry.apiUrl,
      ...body,
      book_id: body.book_id ?? entry.fields.book_id ?? entry.context.book_id,
      ms_since_page_load: Math.round(deps.now()),
      attempt: entry.attempt,
      ...(entry.leftReported && type !== 'left_while_waiting' && { left_reported: true }),
      ...deps.getDevice(),
      ...extra,
    };
    deps.send(payload);
  };

  const take = (source: any): Pending | undefined => {
    const id = source?.[MARK];
    if (typeof id !== 'number') return undefined;
    const entry = pending.get(id);
    pending.delete(id);
    return entry;
  };

  /** Requests that never come back (aborted upstream, answer lost) must not pile up. */
  const prune = () => {
    const cutoff = deps.now() - PENDING_MAX_AGE_MS;
    pending.forEach((entry, id) => {
      if (entry.startedAt < cutoff) pending.delete(id);
    });
    while (pending.size >= PENDING_MAX) {
      const oldest = pending.keys().next().value;
      if (oldest === undefined) break;
      pending.delete(oldest);
    }
  };

  return {
    pendingCount: () => pending.size,

    onRequest<T extends RequestLike>(requestConfig: T): T {
      try {
        if (config().enabled === false) return requestConfig;
        // Outside the booking flow (no page has set a step) nothing is watched.
        if (!context.step) return requestConfig;
        const matched = matchApi(fullUrl(requestConfig), deps.getEndpointMap);
        if (!matched) return requestConfig;
        prune();
        const snapshot = { ...context };
        const fields = pickRequestFields(requestConfig);
        const attemptKey = [
          matched.api.name,
          fields.center_id ?? snapshot.center_id,
          fields.user_center_id ?? snapshot.user_center_id,
          fields.service_id ?? snapshot.service_id,
        ].join('|');
        const attempt = (attempts.get(attemptKey) ?? 0) + 1;
        attempts.set(attemptKey, attempt);
        const id = nextId++;
        pending.set(id, {
          api: matched.api,
          startedAt: deps.now(),
          attempt,
          apiUrl: matched.safeUrl,
          fields,
          context: snapshot,
          leftReported: false,
        });
        (requestConfig as any)[MARK] = id;
      } catch {
        // never block the request
      }
      return requestConfig;
    },

    onResponse(response: ResponseLike) {
      try {
        const entry = take(response?.config);
        if (!entry) return;
        const cfg = config();
        if (cfg.enabled === false) return;
        const duration = Math.round(deps.now() - entry.startedAt);
        const limit = cfg.limits?.[entry.api.name] ?? entry.api.limitMs;
        if (duration > limit) {
          emit('slow_api', entry, { duration_ms: duration, limit_ms: limit }, response);
          return;
        }
        const rate = cfg.ok_sample_rate ?? DEFAULT_OK_SAMPLE_RATE;
        if (rate > 0 && deps.random() < rate) {
          emit('api_ok', entry, { duration_ms: duration, limit_ms: limit, sample_rate: rate }, response);
        }
      } catch {
        // swallow
      }
    },

    onError(error: ErrorLike) {
      try {
        const entry = take(error?.config);
        if (!entry) return;
        if (config().enabled === false) return;
        const status = error.response?.status;
        let reason: string | undefined;
        if (typeof status === 'number') {
          if (status >= 500) reason = `http_${status}`;
        } else if (error.code === 'ERR_CANCELED') {
          reason = undefined;
        } else if (error.code === 'ETIMEDOUT' || error.code === 'ECONNABORTED') {
          reason = 'timeout';
        } else {
          reason = 'network';
        }
        if (!reason) return;
        emit('api_failed', entry, { reason, duration_ms: Math.round(deps.now() - entry.startedAt) }, error.response);
      } catch {
        // swallow
      }
    },

    onPageHide(leaveReason: LeaveReason) {
      try {
        const cfg = config();
        if (cfg.enabled === false) return;
        const minWait = cfg.min_wait_ms ?? DEFAULT_MIN_WAIT_MS;
        pending.forEach(entry => {
          if (entry.leftReported) return;
          const waited = Math.round(deps.now() - entry.startedAt);
          if (waited < minWait) return;
          emit('left_while_waiting', entry, { waited_ms: waited, leave_reason: leaveReason });
          entry.leftReported = true;
        });
      } catch {
        // swallow
      }
    },
  };
}

/**
 * Wires the detector to the axios clients and the page lifecycle. Returns a cleanup function.
 * `send` and the GrowthBook reader are passed in so this module stays free of `_app` imports.
 */
export function startUxIssueRum(options: {
  clients: AxiosInstance[];
  send: (payload: UxIssuePayload) => void;
  getFeature: <T>(key: string, fallback: T) => T;
}): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const detector = createUxIssueDetector({
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    send: options.send,
    getConfig: () => options.getFeature<UxIssueConfig>(UX_ISSUE_FEATURE, {}),
    getEndpointMap: feature => options.getFeature<Record<string, string>>(feature, {}),
    random: Math.random,
    getDevice: () => ({
      current_url: `${window.location.origin}${window.location.pathname}`,
      connection: (navigator as any)?.connection?.effectiveType ?? null,
      online: typeof navigator?.onLine === 'boolean' ? navigator.onLine : null,
      user_agent: navigator?.userAgent ?? null,
      build_id: (window as any).__NEXT_DATA__?.buildId ?? null,
      user_id: (window as any).user?.id ?? null,
    }),
  });

  const ejectors = options.clients.map(client => {
    const requestId = client.interceptors.request.use(requestConfig => detector.onRequest(requestConfig));
    const responseId = client.interceptors.response.use(
      response => {
        detector.onResponse(response);
        return response;
      },
      error => {
        detector.onError(error);
        return Promise.reject(error);
      },
    );
    return () => {
      client.interceptors.request.eject(requestId);
      client.interceptors.response.eject(responseId);
    };
  });

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') detector.onPageHide('page_hidden');
  };
  const onPageHide = () => detector.onPageHide('pagehide');
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);

  return () => {
    ejectors.forEach(eject => eject());
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
  };
}
